import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import DemoBadge from "../components/DemoBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { budgetApi, govApi } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const BudgetScanner = () => {
  const [url, setUrl] = useState("");
  const [entity, setEntity] = useState("City of Cedar Falls");
  const [scanResult, setScanResult] = useState(null);
  const [depts, setDepts] = useState([]);
  const [flags, setFlags] = useState([]);
  const [narrative, setNarrative] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    budgetApi.departments(entity).then(d => setDepts(d.departments || []));
    budgetApi.flags(entity).then(d => setFlags(d.risk_flags || []));
  }, [entity]);

  const runScan = async () => {
    if (!url) { toast.error("Enter a public-sector URL to scan"); return; }
    try {
      const r = await govApi.scan(url, entity);
      setScanResult(r);
      toast.success("Public documents discovered (simulated mode)");
    } catch (e) { toast.error("Scan failed"); }
  };

  const runAnalyze = async () => {
    setLoading(true);
    try {
      const r = await budgetApi.analyze(entity);
      setNarrative(r.narrative || "");
      toast.success("AI analysis complete");
    } catch { toast.error("Analysis failed"); }
    setLoading(false);
  };

  const chartData = depts.map(d => ({
    name: d.name.length > 16 ? d.name.slice(0, 14) + "…" : d.name,
    FY24: d.fy2024 / 1e6,
    FY25: d.fy2025 / 1e6,
  }));

  return (
    <Layout>
      <SEOHead title="Government Budget Scanner | AuditIQ" path="/budget-scanner"
        description="AI-assisted discovery of publicly-available budget documents with risk scoring — respects robots.txt, public pages only." />
      <PageHeader eyebrow="Budget Scanner" title="Scan public government budgets. Surface risk."
        subtitle="Enter a city/county/agency URL. AuditIQ discovers public budget, CIP, and procurement documents, then flags potential variances with non-accusatory language."
        demo
      />
      <section className="max-w-[1400px] mx-auto px-6 py-14">
        <div className="panel-elev p-6 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Public URL</label>
            <Input value={url} onChange={e => setUrl(e.target.value)}
              placeholder="https://www.city.gov"
              className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
              data-testid="scan-url-input" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Entity name</label>
            <Input value={entity} onChange={e => setEntity(e.target.value)}
              className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
              data-testid="scan-entity-input" />
          </div>
          <Button className="btn-gold h-11 px-5" onClick={runScan} data-testid="scan-btn">Discover Documents</Button>
        </div>
        {scanResult && (
          <div className="mt-6 panel p-6" data-testid="scan-results">
            <div className="flex items-center gap-3 mb-3">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Discovered ({scanResult.mode})</div>
              <DemoBadge />
            </div>
            <ul className="space-y-2">
              {scanResult.discovered_documents.map((d) => (
                <li key={d.url} className="flex items-center justify-between border-b border-gold-dim/15 py-2">
                  <div>
                    <div className="text-cream">{d.title}</div>
                    <div className="text-xs text-steel">{d.url}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{d.type}</span>
                </li>
              ))}
            </ul>
            <div className="text-xs text-cream-dim mt-4">{scanResult.note}</div>
          </div>
        )}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-14 grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
        <div className="panel-elev p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl">Department Spending — FY24 vs FY25</h3>
            <DemoBadge />
          </div>
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 16, bottom: 40, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a3b5f" />
                <XAxis dataKey="name" stroke="#D8D2C5" fontSize={11} angle={-22} textAnchor="end" height={60} />
                <YAxis stroke="#D8D2C5" fontSize={11} tickFormatter={(v) => `$${v}M`} />
                <Tooltip contentStyle={{ background: "#152A52", border: "1px solid #C9A84C", color: "#F4EFE6" }}
                  formatter={(v) => `$${v.toFixed(2)}M`} />
                <Bar dataKey="FY24" fill="#4A5568" radius={[2, 2, 0, 0]} />
                <Bar dataKey="FY25" fill="#C9A84C" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="panel-elev p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl">Risk Flags</h3><DemoBadge />
          </div>
          <ul className="space-y-3 max-h-80 overflow-auto pr-2">
            {flags.map(f => (
              <li key={f.id} className="border-l-2 pl-3 py-1" style={{
                borderColor: f.severity === "high" ? "var(--auditiq-critical)" :
                             f.severity === "medium" ? "var(--auditiq-warning)" : "var(--auditiq-success)"
              }}>
                <div className="text-sm text-cream">{f.flag_type}</div>
                <div className="text-xs text-cream-dim mt-1">{f.description}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gold mt-1">{f.language}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-20">
        <div className="panel-elev p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h3 className="font-display text-xl">AI Executive Summary</h3>
              <div className="text-xs text-cream-dim">Claude Sonnet 4.5 · non-accusatory · decision-support only</div>
            </div>
            <Button className="btn-gold h-10 px-5" onClick={runAnalyze} disabled={loading} data-testid="analyze-btn">
              {loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analyzing…</> : "Generate AI Analysis"}
            </Button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-cream-dim font-sans leading-relaxed min-h-[120px]" data-testid="analysis-output">
            {narrative || "Click Generate to run a preliminary analysis against the demo budget data."}
          </pre>
        </div>
      </section>
    </Layout>
  );
};

export default BudgetScanner;
